import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { checkBrand, slugify, summarise, type BrandInput } from '@motorcity/shared';
import { useAuth } from '../auth/AuthProvider';
import {
  useBrandTree,
  useCreateModel,
  useDeleteModel,
  useReorderModels,
  useSaveBrand,
} from '../api/hooks';
import { Breadcrumbs, ErrorNote, PageHeader } from '../components/Layout';
import { AssetField } from '../components/AssetField';
import { LocalizedInput, StatusSelect } from '../components/Fields';
import { LocaleSwitch, countMissing } from '../components/EditLocale';
import { LiveWarning, ReadinessPanel } from '../components/Readiness';
import { CardGrid, type GridCard } from '../components/CardGrid';
import { SaveBar } from '../components/SaveBar';
import { EmptyState, FormSkeleton, GridSkeleton, SectionHeader } from '../components/ui/primitives';
import { errorMessage, useToast } from '../components/ui/Toast';
import { useDraft, useUnsavedWarning } from '../hooks/useDraft';
import { emptyVehicle } from '../components/VehicleForm';
import { getConfig } from '../config';
import type { ApiError } from '../api/client';

export function BrandPage() {
  const { brandId = '' } = useParams();
  const { user } = useAuth();
  const tree = useBrandTree(brandId);
  const save = useSaveBrand(brandId);
  const reorder = useReorderModels(brandId);
  const removeModel = useDeleteModel(brandId);
  const toast = useToast();

  const serverValue: BrandInput | undefined = tree.data
    ? {
        name: tree.data.brand.name,
        feedKey: tree.data.brand.feedKey,
        description: tree.data.brand.description,
        logo: tree.data.brand.logo,
        coverImage: tree.data.brand.coverImage,
        sortOrder: tree.data.brand.sortOrder,
        status: tree.data.brand.status,
      }
    : undefined;

  const { draft, setDraft, dirty, commit, reset } = useDraft(serverValue);
  useUnsavedWarning(dirty);
  const [addingModel, setAddingModel] = useState(false);

  if (tree.isLoading || !draft) {
    return (
      <div className="page">
        <PageHeader title="Loading…" />
        <FormSkeleton />
        <GridSkeleton cards={3} />
      </div>
    );
  }
  if (tree.error) return <ErrorNote error={tree.error} />;
  if (!tree.data) return <ErrorNote error={new Error('Brand not found')} />;

  const set = <K extends keyof BrandInput>(key: K, value: BrandInput[K]) =>
    setDraft({ ...draft, [key]: value });

  const fieldErrors = (save.error as ApiError | undefined)?.fields;
  const missing = countMissing([draft.name, draft.description]);
  const readiness = summarise(checkBrand(draft));

  const models: GridCard[] = tree.data.models.map((model) => {
    const variantCount = tree.data!.variants.filter((v) => v.modelId === model.modelId).length;
    const price = model.startingPrice;
    return {
      id: model.modelId,
      title: model.name.en,
      subtitle: model.name.ar || undefined,
      facts: [
        `${variantCount} variant${variantCount === 1 ? '' : 's'}`,
        ...(model.colors?.length ? [`${model.colors.length} colours`] : []),
        ...(price ? [`from ${price.currency} ${price.amount.toLocaleString()}`] : []),
      ],
      status: model.status,
      href: `/brands/${brandId}/models/${model.modelId}`,
      // Tactical shots are photography, so they fill the tile rather than
      // sitting inside it like a logo.
      fit: 'cover' as const,
      imageUrl: model.tacticalImage
        ? `${getConfig().contentBaseUrl}/${model.tacticalImage.key}`
        : undefined,
    };
  });

  const onSave = () =>
    save.mutate(draft, {
      onSuccess: () => {
        commit(draft);
        toast.success('Brand saved');
      },
      onError: (err) => toast.error(errorMessage(err)),
    });

  return (
    <div className="page wide">
      <Breadcrumbs
        trail={[{ label: 'Brands', to: '/brands' }, { label: draft.name.en || brandId }]}
      />

      <PageHeader title={draft.name.en || brandId} slug={brandId}>
        <LocaleSwitch missing={missing} />
      </PageHeader>

      <ReadinessPanel summary={readiness} status={draft.status} entity="brand" />

      <section className="card">
        <SectionHeader
          title="Brand"
          description="Shown on the app's landing screen and at the top of the brand page."
        />
        <LocalizedInput
          label="Name"
          required
          value={draft.name}
          onChange={(name) => set('name', name)}
          error={fieldErrors?.name?.[0]}
        />
        <LocalizedInput
          label="Description"
          multiline
          rows={5}
          value={draft.description}
          onChange={(description) => set('description', description)}
        />
        <div className="grid-2">
          <AssetField
            label="Logo"
            hint="Transparent PNG or SVG works best."
            prefix={`brands/${brandId}`}
            value={draft.logo}
            onChange={(logo) => set('logo', logo)}
          />
          <AssetField
            label="Cover image"
            hint="Wide hero behind the brand name."
            prefix={`brands/${brandId}`}
            value={draft.coverImage}
            onChange={(coverImage) => set('coverImage', coverImage)}
          />
        </div>
        <label className="field">
          <span className="field-label">Legacy feed key</span>
          <input
            className="input mono"
            value={draft.feedKey}
            placeholder={draft.name.en || 'auto'}
            onChange={(e) => set('feedKey', e.target.value)}
          />
          <span className="field-hint">
            The key this brand appears under in <code>/mobapp/v1.json</code>. Preserved from the old
            feed — which spelled some brands <code>chery</code> and <code>King Long</code> — so
            existing app builds keep matching. Blank falls back to the English name.
          </span>
        </label>

        <StatusSelect
          value={draft.status}
          onChange={(status) => set('status', status)}
          warning={<LiveWarning summary={readiness} />}
        />
      </section>

      <section className="card">
        <SectionHeader
          title="Models"
          description="Drag order here is the order the app shows them in."
          action={
            <button type="button" className="btn" onClick={() => setAddingModel(true)}>
              New model
            </button>
          }
        />

        {addingModel ? (
          <NewModelForm brandId={brandId} onDone={() => setAddingModel(false)} />
        ) : null}

        {models.length === 0 && !addingModel ? (
          <EmptyState
            title="No models yet"
            body="Add the first model under this brand."
            action={
              <button type="button" className="btn primary" onClick={() => setAddingModel(true)}>
                New model
              </button>
            }
          />
        ) : (
          <CardGrid
            items={models}
            canDelete={Boolean(user?.isAdmin)}
            onReorder={(ids) => reorder.mutate(ids)}
            onDelete={(id) => {
              if (window.confirm(`Delete model "${id}" and all of its variants?`)) {
                removeModel.mutate(id, {
                  onSuccess: () => toast.success(`Deleted ${id}`),
                  onError: (err) => toast.error(errorMessage(err)),
                });
              }
            }}
          />
        )}
      </section>

      <SaveBar
        dirty={dirty}
        saving={save.isPending}
        error={save.error}
        onReset={reset}
        onSave={onSave}
      />
    </div>
  );
}

function NewModelForm({ brandId, onDone }: { brandId: string; onDone: () => void }) {
  const create = useCreateModel(brandId);
  const toast = useToast();
  const [nameEn, setNameEn] = useState('');
  const modelId = slugify(nameEn);

  return (
    <div className="inline-form">
      <div className="row">
        <input
          className="input"
          placeholder="Model name (English)"
          autoFocus
          value={nameEn}
          onChange={(e) => setNameEn(e.target.value)}
        />
        <code className="slug">{modelId || '—'}</code>
        <button type="button" className="btn subtle" onClick={onDone}>
          Cancel
        </button>
        <button
          type="button"
          className="btn primary"
          data-loading={create.isPending || undefined}
          disabled={modelId.length < 2 || create.isPending}
          onClick={() =>
            create.mutate(
              {
                ...emptyVehicle(),
                colors: [],
                marketing: {
                  vehicleType: 'Passenger',
                  category: '',
                  featured: false,
                  testDriveAvailable: true,
                  externalLink: '',
                  quoteUrl: '',
                  testDriveUrl: '',
                  feedId: '',
                },
                modelId,
                name: { en: nameEn.trim(), ar: '' },
              },
              {
                onSuccess: (model) => {
                  toast.success(`Created ${model.name.en}`);
                  onDone();
                },
                onError: (err) => toast.error(errorMessage(err)),
              },
            )
          }
        >
          Create
        </button>
      </div>
      {create.error ? <ErrorNote error={create.error} /> : null}
    </div>
  );
}
